import * as actions from './actions';

import { ResourcesAPIResponse, ResourcesResponse } from 'store/resources/types';
import { catchError, expand, filter, map, reduce, switchMap, withLatestFrom } from 'rxjs/operators';

import { AjaxResponse } from 'rxjs/ajax';
import { Epic } from 'redux-observable';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import { RootState } from 'store/reducer';
import { isActionOf } from 'typesafe-actions';
import { EMPTY, of } from 'rxjs';
import { transformErrorIntoSerializableError } from 'store/utils';
import { getSelectedAppInstanceConfigs } from 'utils/getSelectedAppInstanceConfigs';
import { RESOURCES_MAX_PAGES, RESOURCES_PAGE_SIZE } from './constants';

/*
 * ECHO-115 (was RES-54). helix answers one page per request, 50 by default and 100 at most, and applies
 * audiences AFTER cutting the page, so a list read in one request silently stopped at "50 minus whatever
 * this user may not see" — 36 of 48 visible rooms at a 65-room building. Page until helix's `total` (the
 * pre-audience row count) is covered. A caller that sets its own `limit`/`offset` keeps its single request.
 */
// The page constants live in ./constants: the root epic combines every export of this module as an epic.

export const getResourcesEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getResources.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      const { ownerType, ownerUuid, filterParams } = payload;
      const selectedAppInstanceConfigs = getSelectedAppInstanceConfigs(state.appInstanceConfigs);
      const client = apiClient(state);
      const callerPages = filterParams?.limit !== undefined || filterParams?.offset !== undefined;

      if (callerPages) {
        return client.getResources(ownerType, ownerUuid, filterParams, selectedAppInstanceConfigs).pipe(
          map(({ response }: AjaxResponse<ResourcesAPIResponse>) =>
            actions.getResources.success({
              resources: response.data.resources,
              total: response.total,
              paging: response.paging,
            }),
          ),
          catchError((error: Error) =>
            of(actions.getResources.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
      }

      const page = (offset: number) =>
        client
          .getResources(
            ownerType,
            ownerUuid,
            { ...filterParams, limit: RESOURCES_PAGE_SIZE, offset },
            selectedAppInstanceConfigs,
          )
          .pipe(map(({ response }: AjaxResponse<ResourcesAPIResponse>) => ({ response, offset })));

      return page(0).pipe(
        expand(({ response, offset }) => {
          const next = offset + RESOURCES_PAGE_SIZE;
          // Not "stop on an empty page": audiences are applied after the cut, so a page can come back empty
          // while later pages still hold rooms this user may see. `total` and the page cap bound the loop.
          const isDone = next >= (response.total ?? 0);

          return isDone || next >= RESOURCES_PAGE_SIZE * RESOURCES_MAX_PAGES ? EMPTY : page(next);
        }),
        reduce<{ response: ResourcesAPIResponse; offset: number }, ResourcesResponse>(
          (all, { response }) => ({
            resources: [...all.resources, ...(response.data.resources ?? [])],
            total: response.total,
            paging: response.paging,
          }),
          { resources: [], total: 0, paging: { next: '', previous: '' } },
        ),
        map(result => actions.getResources.success(result)),
        catchError((error: Error) =>
          of(actions.getResources.failure({ error: transformErrorIntoSerializableError(error) })),
        ),
      );
    }),
  );

export const getInfiniteResourcesEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.getInfiniteResources.request)),
    withLatestFrom(state$),
    switchMap(([{ payload }, state]) => {
      const { ownerType, ownerUuid, filterParams } = payload;
      const selectedAppInstanceConfigs = getSelectedAppInstanceConfigs(state.appInstanceConfigs);

      return apiClient(state)
        .getResources(ownerType, ownerUuid, filterParams, selectedAppInstanceConfigs)
        .pipe(
          map(({ response }: AjaxResponse<ResourcesAPIResponse>) => {
            return actions.getInfiniteResources.success({
              resources: response.data.resources,
              paging: response.paging,
              indexOfSet: payload.indexOfSet,
              total: response.total,
            });
          }),
          catchError((error: Error) =>
            of(actions.getInfiniteResources.failure({ error: transformErrorIntoSerializableError(error) })),
          ),
        );
    }),
  );
